import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bot, X, User, FileText, FileAudio } from "lucide-react";

const ANIM_MS = 280; // animation duration (ms)

// Base (non-animated) styles
const baseOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  zIndex: 999,
};

const baseSidebar = {
  position: "fixed",
  top: 0,
  right: 0,
  width: "50vw",
  maxWidth: "100vw",
  height: "100vh",
  background: "#fff",
  boxShadow: "-2px 0 16px #0002",
  zIndex: 1000,
  padding: 32,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
};

const closeBtnStyle = {
  position: "absolute",
  top: 16,
  right: 24,
  fontSize: 28,
  background: "none",
  border: "none",
  cursor: "pointer",
};

const TranscriptPlayer = ({ audioUrl, transcriptUrl, callsummary, onClose, transcriberHref, transcriberTitle, callRecordHref, callRecordTitle, shareableLink, standalone }) => {
  const [segments, setSegments] = useState([]);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0); // audio playback time (for voice only)
  const [transcriptTime, setTranscriptTime] = useState(0); // transcript time (for word highlighting)
  const [visible, setVisible] = useState(false); // animation flag
  const [copied, setCopied] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);

  const audioRef = useRef(null);
  const activeWordRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const userScrolledRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Lock background scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Animate in on mount
  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setVisible(true);
    } else {
      requestAnimationFrame(() => setVisible(true));
    }
  }, []);

  // Close (wait for exit animation)
  const handleClose = () => {
    if (audioRef.current) audioRef.current.pause();
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      onClose?.();
      return;
    }
    setVisible(false);
    setTimeout(() => onClose?.(), ANIM_MS);
  };

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Load transcript JSON (filter to user/bot)
  useEffect(() => {
    if (!transcriptUrl) {
      setSegments([]);
      return;
    }
    fetch(transcriptUrl)
      .then((res) => res.json())
      .then((data) =>
        setSegments((data || []).filter((seg) => seg.role === "user" || seg.role === "bot"))
      )
      .catch(() => setSegments([]));
  }, [transcriptUrl]);

  // Build word timings (handles missing secondsFromStart by accumulating)
  const { wordTimings, currentSegIdx, currentWordIdx, transcriptStartTime, transcriptEndTime, transcriptDuration } = useMemo(() => {
    const timings = [];
    let accStart = 0;
    let cSeg = -1;
    let cWord = -1;
    let firstStartTime = Infinity;
    let lastEndTime = 0;

    segments.forEach((seg, segIdx) => {
      const segWords = (seg.message || "").split(" ");
      const segDuration = ((seg.endTime ? seg.endTime - seg.time : seg.duration) || 0) / 1000;
      const segStart =
        typeof seg.secondsFromStart === "number" ? seg.secondsFromStart : accStart;
      const wordDuration = segWords.length ? segDuration / segWords.length : 0;

      segWords.forEach((word, wIdx) => {
        const start = segStart + wIdx * wordDuration;
        const end = segStart + (wIdx + 1) * wordDuration;
        timings.push({ segIdx, wIdx, word, start, end });
      });

      accStart = segStart + segDuration;
      firstStartTime = Math.min(firstStartTime, segStart);
      lastEndTime = Math.max(lastEndTime, accStart);
    });

    const transcriptStart = firstStartTime === Infinity ? 0 : firstStartTime;
    const transcriptDur = lastEndTime - transcriptStart;

    // find active word by currentTime in outer state (not available here), return placeholders; actual highlighting computed below
    return { 
      wordTimings: timings, 
      currentSegIdx: cSeg, 
      currentWordIdx: cWord, 
      transcriptStartTime: transcriptStart,
      transcriptEndTime: lastEndTime,
      transcriptDuration: transcriptDur
    };
  }, [segments]);

  // Map audio playback time to transcript time using transcript as source of truth
  useEffect(() => {
    if (audioDuration > 0 && transcriptDuration > 0 && audioCurrentTime >= 0) {
      // Improved mapping: account for transcript start time and duration
      // Map audio time proportionally to transcript duration, then add transcript start offset
      const ratio = Math.min(1, Math.max(0, audioCurrentTime / audioDuration));
      const mappedTranscriptTime = transcriptStartTime + (ratio * transcriptDuration);
      setTranscriptTime(mappedTranscriptTime);
    } else if (transcriptEndTime > 0 && audioDuration === 0) {
      // If audio not loaded yet but transcript is, use audio time directly as fallback
      // But account for transcript start time
      setTranscriptTime(Math.max(transcriptStartTime, audioCurrentTime));
    } else if (transcriptEndTime > 0) {
      // If we have transcript but no audio yet, use transcript start time
      setTranscriptTime(transcriptStartTime);
    } else {
      setTranscriptTime(0);
    }
  }, [audioCurrentTime, audioDuration, transcriptStartTime, transcriptEndTime, transcriptDuration]);

  // Determine active word based on transcript time (transcript is source of truth)
  const active = useMemo(() => {
    return wordTimings.find((w) => transcriptTime >= w.start && transcriptTime < w.end);
  }, [wordTimings, transcriptTime]);
  const aSeg = active ? active.segIdx : -1;
  const aWord = active ? active.wIdx : -1;

  // User scroll override
  useEffect(() => {
    const container = transcriptContainerRef.current;
    if (!container) return;
    const handleUserScroll = () => {
      userScrolledRef.current = true;
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        userScrolledRef.current = false;
        if (activeWordRef.current) {
          activeWordRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }, 2000);
    };
    container.addEventListener("scroll", handleUserScroll);
    return () => {
      container.removeEventListener("scroll", handleUserScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  // Auto-scroll when not manually scrolling
  useEffect(() => {
    if (!userScrolledRef.current && activeWordRef.current) {
      activeWordRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [transcriptTime]);

  // Animated styles
  const overlayStyle = standalone ? {} : {
    ...baseOverlay,
    opacity: visible ? 1 : 0,
    transition: `opacity ${ANIM_MS}ms ease`,
  };
  const sidebarStyle = standalone ? {
    position: 'relative',
    width: '100%',
    maxWidth: 700,
    margin: '0 auto',
    background: '#fff',
    boxShadow: '0 2px 16px #0001',
    borderRadius: 16,
    padding: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  } : {
    ...baseSidebar,
    transform: visible ? "translateX(0)" : "translateX(100%)",
    opacity: visible ? 1 : 0.98,
    transition: `transform ${ANIM_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${ANIM_MS}ms ease`,
  };

  const content = (
    <>
      {!standalone && <div style={overlayStyle} onClick={handleClose} />}
      <div style={sidebarStyle} role="dialog" aria-modal={!standalone}>
        {onClose && (
          <button onClick={handleClose} style={closeBtnStyle} aria-label="Close transcript">
            <X size={24} />
          </button>
        )}

        <h2 style={{ marginBottom: 8, fontSize: 20, color: "#374151" }}>Call Review</h2>

        {callsummary && (
          <div
            style={{
              background: "#FFFBEA",
              border: "2px solid #FACC15",
              borderRadius: 12,
              padding: "12px 14px",
              marginBottom: 16,
              color: "#7A5200",
              boxShadow: "0 1px 4px rgba(17,24,39,0.06)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: "#F59E0B",
                  }}
                />
                <div style={{ fontWeight: 700, fontSize: 16 }}>Call Summary</div>
              </div>
            </div>
            <div style={{ color: "#374151", fontSize: 15, lineHeight: 1.6 }}>{callsummary}</div>
          </div>
        )}

        <audio
          ref={audioRef}
          src={audioUrl}
          controls
          onTimeUpdate={(e) => setAudioCurrentTime(e.target.currentTime)}
          onLoadedMetadata={(e) => {
            const duration = e.target.duration;
            if (duration && isFinite(duration)) {
              setAudioDuration(duration);
            }
          }}
          style={{
            width: "100%",
            marginBottom: 16,
            background: "#e6f2ff",
            borderRadius: 12,
            padding: 8,
            boxSizing: "border-box",
          }}
        />

        {/* Shareable link section */}
        {shareableLink && (
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text"
              value={shareableLink}
              readOnly
              style={{ flex: 1, fontSize: 13, padding: 6, border: '1px solid #ccc', borderRadius: 6, background: '#f9fafb' }}
              onFocus={e => e.target.select()}
            />
            <button
              className="btn btn-secondary"
              style={{ fontSize: 13, padding: '6px 12px' }}
              onClick={() => {
                navigator.clipboard.writeText(shareableLink);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              title="Copy shareable page link"
            >
              {copied ? 'Copied!' : 'Copy Page Link'}
            </button>
          </div>
        )}

        {/* Transcript */}
        <div
          ref={transcriptContainerRef}
          style={{
            flex: 1,
            overflowY: "auto",
            borderRadius: 12,
            padding: 2,
            color: "#374151",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {segments.map((seg, segIdx) => {
            const segWords = (seg.message || "").split(" ");
            const isAdvisor = seg.role === "user"; // adjust if needed

            return (
              <div
                key={segIdx}
                style={{
                  display: "flex",
                  justifyContent: isAdvisor ? "flex-start" : "flex-end",
                  alignItems: "center",
                  width: "100%",
                  gap: 6,
                }}
              >
                {isAdvisor && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      width: 40,
                      height: 40,
                      backgroundColor: "#E0E7FF",
                    }}
                  >
                    <User />
                  </div>
                )}

                <div
                  style={{
                    maxWidth: "85%",
                    borderRadius: 14,
                    padding: "8px 12px",
                    boxShadow: "0 1px 4px rgba(17,24,39,0.06)",
                    border: "1px solid",
                    borderColor: isAdvisor ? "#BFDBFE" : "#DDD6FE",
                    background: isAdvisor ? "#EFF6FF" : "#F5F3FF",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    width: "100%",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 12.5,
                      color: isAdvisor ? "#1D4ED8" : "#6D28D9",
                      opacity: 0.9,
                    }}
                  >
                    {isAdvisor ? "Advisor" : "Customer"}
                  </span>

                  <div style={{ fontSize: 15, display: "flex", flexWrap: "wrap" }}>
                    {segWords.map((word, wIdx) => {
                      const isActive = segIdx === aSeg && wIdx === aWord;
                      // find timing for seeking
                      const timing = wordTimings.find(
                        (w) => w.segIdx === segIdx && w.wIdx === wIdx
                      );
                      return (
                        <span
                          key={wIdx}
                          ref={isActive ? activeWordRef : null}
                          style={{
                            background: isActive ? "#FEF08A" : "transparent",
                            color: isActive ? "#111827" : "#374151",
                            borderRadius: 4,
                            padding: "2px",
                            marginRight: 2,
                            fontWeight: isActive ? 700 : 400,
                            transition: "background .2s, color .2s",
                            cursor: timing ? "pointer" : undefined,
                          }}
                          onClick={() => {
                            if (timing && audioRef.current && audioDuration > 0 && transcriptDuration > 0) {
                              // Map transcript time to audio time
                              // Account for transcript start time offset
                              const transcriptOffset = timing.start - transcriptStartTime;
                              const ratio = Math.min(1, Math.max(0, transcriptOffset / transcriptDuration));
                              const audioTime = ratio * audioDuration;
                              audioRef.current.currentTime = Math.max(0, audioTime);
                              audioRef.current.play();
                            }
                          }}
                        >
                          {word}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {!isAdvisor && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      width: 40,
                      height: 40,
                      backgroundColor: "#E0E7FF",
                    }}
                  >
                    <Bot />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Download buttons at the bottom of the sidebar */}
      {/* (Removed: now at top in summary card) */}
    </>
  );

  // Render as sidebar (portal) or as block (standalone)
  if (standalone) {
    return content;
  }
  return createPortal(content, document.body);
};

export default TranscriptPlayer;
